import { useMemo } from 'react';
import { useVitaEngine } from '../hooks/useVitaEngine';

function AnalyticsPanel({
  projectActors = [],
  projectAlerts = [],
  projectSignals = []
}) {
  const { processedActors } = useVitaEngine(
    projectActors,
    projectAlerts,
    projectSignals
  );

  const stats = useMemo(() => {
    const porEstado = {};
    const porInfluencia = {};
    const narrativaConteo = {};

    processedActors.forEach((actor) => {
      const estado = actor._vita?.state?.state || 'Sin estado';
      porEstado[estado] = (porEstado[estado] || 0) + 1;

      const inf = actor.influencia || 'Sin influencia';
      porInfluencia[inf] = (porInfluencia[inf] || 0) + 1;

      const narrativas = Array.isArray(actor.narrativas) ? [...actor.narrativas] : [];
      if (actor.narrativaDominante) narrativas.push(actor.narrativaDominante);
      narrativas.forEach((n) => {
        if (!n) return;
        const key = typeof n === 'string' ? n.trim() : (n?.texto || '').trim();
        if (!key) return;
        narrativaConteo[key] = (narrativaConteo[key] || 0) + 1;
      });
    });

    const ivtPromedio = processedActors.length > 0
      ? Math.round(
          processedActors.reduce((s, a) => s + (a._vita?.scoring?.IVT || 0), 0)
          / processedActors.length
        )
      : 0;

    const ivtMaximo = processedActors.reduce(
      (max, a) => Math.max(max, a._vita?.scoring?.IVT || 0), 0
    );

    const actoresCriticos = processedActors.filter((a) =>
      ['Escalamiento Territorial', 'Convergencia Narrativa', 'Activación Territorial']
        .includes(a._vita?.state?.state)
    ).length;

    const narrativasOrdenadas = Object.entries(narrativaConteo)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    const estadosOrdenados = Object.entries(porEstado)
      .sort((a, b) => estadoOrden(a[0]) - estadoOrden(b[0]));

    return {
      ivtPromedio,
      ivtMaximo,
      actoresCriticos,
      porEstado: estadosOrdenados,
      porInfluencia: Object.entries(porInfluencia).sort((a, b) => b[1] - a[1]),
      narrativasOrdenadas
    };
  }, [processedActors]);

  const alertasActivas = projectAlerts.filter((a) => a.estado !== 'Cerrada').length;
  const señalesCriticas = projectSignals.filter(
    (s) => s.sentimiento === 'Negativo' || s.riesgo === 'Alto' || s.riesgo === 'Crítico'
  ).length;

  return (
    <div>
      <div style={styles.header}>
        <div style={styles.badge}>VITA ENGINE</div>
        <h1 style={styles.title}>Analítica Territorial Cognitiva</h1>
        <p style={styles.subtitle}>
          Lectura del motor territorial — IVT, estados cognitivos y narrativas activas.
        </p>
      </div>

      {/* KPIs cognitivos */}
      <div style={styles.kpiGrid}>
        <KPI label="IVT Promedio" value={`${stats.ivtPromedio}%`} color="#0993b5" desc="Índice de viabilidad territorial promedio" />
        <KPI label="IVT Máximo" value={`${stats.ivtMaximo}%`} color="#ef4444" desc="Actor con mayor presión territorial" />
        <KPI label="Actores críticos" value={stats.actoresCriticos} color="#f97316" desc="En activación, convergencia o escalamiento" />
        <KPI label="Alertas activas" value={alertasActivas} color="#eab308" desc="Eventos territoriales en seguimiento" />
        <KPI label="Señales RRSS críticas" value={señalesCriticas} color="#dc2626" desc="Señales negativas o de alto riesgo" />
      </div>

      <div style={styles.grid}>

        {/* Estados territoriales */}
        <div style={styles.panel}>
          <div style={styles.panelLabel}>DISTRIBUCIÓN DE ESTADOS TERRITORIALES</div>
          {stats.porEstado.length === 0
            ? <p style={styles.empty}>Sin actores procesados.</p>
            : stats.porEstado.map(([estado, cantidad]) => {
                const color = estadoColor(estado);
                const pct = Math.round((cantidad / processedActors.length) * 100);
                return (
                  <div key={estado} style={styles.barRow}>
                    <div style={styles.barLabel}>
                      <span style={{ color, fontWeight: '700' }}>{estado}</span>
                      <span style={styles.barCount}>{cantidad} actor{cantidad !== 1 ? 'es' : ''}</span>
                    </div>
                    <div style={styles.barTrack}>
                      <div style={{
                        ...styles.barFill,
                        width: `${pct}%`,
                        background: color
                      }} />
                    </div>
                  </div>
                );
              })
          }
        </div>

        {/* Narrativas */}
        <div style={styles.panel}>
          <div style={styles.panelLabel}>NARRATIVAS TERRITORIALES MÁS EXTENDIDAS</div>
          {stats.narrativasOrdenadas.length === 0
            ? <p style={styles.empty}>Sin narrativas registradas.</p>
            : stats.narrativasOrdenadas.map(([narrativa, cantidad], i) => (
                <div key={i} style={styles.narrativaRow}>
                  <div style={{
                    ...styles.narrativaDot,
                    background: i === 0 ? '#0993b5' : '#e4e7ec'
                  }} />
                  <div style={styles.narrativaTexto}>
                    <div style={styles.narrativaLabel}>{narrativa}</div>
                    <div style={styles.narrativaMeta}>
                      {cantidad} actor{cantidad !== 1 ? 'es' : ''} · {cantidad >= 3 ? 'Convergencia detectada' : cantidad >= 2 ? 'Presencia compartida' : 'Señal individual'}
                    </div>
                  </div>
                  <div style={{
                    ...styles.narrativaBadge,
                    background: cantidad >= 3 ? 'rgba(239,68,68,0.10)' : 'rgba(9,147,181,0.10)',
                    color: cantidad >= 3 ? '#b91c1c' : '#0993b5'
                  }}>
                    {cantidad}
                  </div>
                </div>
              ))
          }
        </div>

        {/* IVT por actor */}
        <div style={{ ...styles.panel, gridColumn: 'span 2' }}>
          <div style={styles.panelLabel}>IVT POR ACTOR — ÍNDICE DE VIABILIDAD TERRITORIAL</div>
          {processedActors.length === 0
            ? <p style={styles.empty}>Sin actores registrados.</p>
            : [...processedActors]
                .sort((a, b) => (b._vita?.scoring?.IVT || 0) - (a._vita?.scoring?.IVT || 0))
                .map((actor) => {
                  const ivt = actor._vita?.scoring?.IVT || 0;
                  const estado = actor._vita?.state?.state || '-';
                  const color = estadoColor(estado);
                  return (
                    <div key={actor.id} style={styles.actorIvtRow}>
                      <div style={styles.actorIvtLeft}>
                        <div style={styles.actorIvtNombre}>{actor.nombre}</div>
                        <div style={{ color, fontSize: '12px', fontWeight: '700' }}>{estado}</div>
                      </div>
                      <div style={styles.actorIvtBar}>
                        <div style={styles.barTrack}>
                          <div style={{
                            ...styles.barFill,
                            width: `${ivt}%`,
                            background: color
                          }} />
                        </div>
                      </div>
                      <div style={{ ...styles.ivtValue, color }}>{ivt}%</div>
                    </div>
                  );
                })
          }
        </div>

      </div>
    </div>
  );
}

function KPI({ label, value, color, desc }) {
  return (
    <div style={styles.kpiCard}>
      <div style={{ ...styles.kpiValue, color }}>{value}</div>
      <div style={styles.kpiLabel}>{label}</div>
      <div style={styles.kpiDesc}>{desc}</div>
    </div>
  );
}

function estadoOrden(estado) {
  const order = {
    'Escalamiento Territorial': 0,
    'Convergencia Narrativa': 1,
    'Activación Territorial': 2,
    'Sensibilidad Creciente': 3,
    'Observación': 4,
    'Estable': 5
  };
  return order[estado] ?? 9;
}

function estadoColor(estado) {
  return {
    'Escalamiento Territorial': '#ef4444',
    'Convergencia Narrativa': '#f97316',
    'Activación Territorial': '#eab308',
    'Sensibilidad Creciente': '#22c55e',
    'Observación': '#06b6d4',
    'Estable': '#64748b'
  }[estado] || '#94a3b8';
}

const styles = {
  header: { marginBottom: '28px' },
  badge: {
    display: 'inline-block',
    background: 'linear-gradient(135deg,#1fa97a,#0993b5)',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '999px',
    fontSize: '10px',
    fontWeight: '900',
    letterSpacing: '1px',
    marginBottom: '12px'
  },
  title: { margin: 0, fontSize: '34px', color: '#ffffff' },
  subtitle: { color: 'rgba(255,255,255,0.55)', marginTop: '6px' },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
    gap: '16px',
    marginBottom: '24px'
  },
  kpiCard: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '18px',
    padding: '22px'
  },
  kpiValue: {
    fontSize: '36px',
    fontWeight: '900',
    lineHeight: '1',
    marginBottom: '8px'
  },
  kpiLabel: {
    color: 'white',
    fontSize: '14px',
    fontWeight: '700',
    marginBottom: '4px'
  },
  kpiDesc: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: '12px',
    lineHeight: '1.4'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px'
  },
  panel: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '18px',
    padding: '24px'
  },
  panelLabel: {
    color: '#67e8f9',
    fontSize: '10px',
    fontWeight: '900',
    letterSpacing: '1.2px',
    marginBottom: '18px'
  },
  empty: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: '13px',
    fontStyle: 'italic'
  },
  barRow: { marginBottom: '14px' },
  barLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '6px',
    fontSize: '13px'
  },
  barCount: { color: 'rgba(255,255,255,0.50)', fontSize: '12px' },
  barTrack: {
    height: '8px',
    background: 'rgba(255,255,255,0.08)',
    borderRadius: '999px',
    overflow: 'hidden'
  },
  barFill: {
    height: '100%',
    borderRadius: '999px',
    transition: 'width 0.4s ease'
  },
  narrativaRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    paddingBottom: '14px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    marginBottom: '14px'
  },
  narrativaDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
    marginTop: '4px'
  },
  narrativaTexto: { flex: 1 },
  narrativaLabel: {
    color: 'rgba(255,255,255,0.88)',
    fontSize: '13px',
    fontWeight: '600',
    lineHeight: '1.4',
    marginBottom: '3px'
  },
  narrativaMeta: {
    color: 'rgba(255,255,255,0.40)',
    fontSize: '11px'
  },
  narrativaBadge: {
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '13px',
    fontWeight: '900',
    flexShrink: 0
  },
  actorIvtRow: {
    display: 'grid',
    gridTemplateColumns: '200px 1fr 52px',
    alignItems: 'center',
    gap: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    marginBottom: '12px'
  },
  actorIvtLeft: {},
  actorIvtNombre: {
    color: 'white',
    fontSize: '14px',
    fontWeight: '700',
    marginBottom: '3px'
  },
  actorIvtBar: { flex: 1 },
  ivtValue: {
    fontSize: '15px',
    fontWeight: '900',
    textAlign: 'right'
  }
};

export default AnalyticsPanel;